import React, { useCallback, useEffect, useState } from 'react';
import {
	Accordion,
	AccordionButton,
	AccordionIcon,
	AccordionItem,
	AccordionPanel,
	Box,
	Flex,
	Icon,
	Image,
	Text,
	useToast
} from '@chakra-ui/react';
import { FiArrowLeft } from 'react-icons/fi';
import { FaRegCheckCircle, FaRegCircle } from 'react-icons/fa';
import { add, format } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';

import { formatPrice } from '../../../utils/format';
import { FinJsonDTO } from '../../../dtos/FinJsonDTO';

import {
  Container,
  Content,
  Header,
  TituloContent,
	TituloItem,
} from './styles';

interface ModalParams {
	id?: string;
	onClose: any;
	isVisible: boolean;
	titulo: FinJsonDTO;
}

const ModalTitulo: React.FC<ModalParams> = ({
  id = 'modalTitulo',
  onClose = () => {},
  isVisible,
	titulo,
}) => {
	const toast = useToast();

  const handleOutSideClick = useCallback(
    (e: any) => {
      if (e.target.id === id) {
        onClose();
      }
    },
    [id, onClose],
  );

	useEffect(() => {
		isVisible = false;
		const existe = titulo.parcelas.findIndex((parcel: any) => parcel.baixado === false);
		titulo.baixado = existe >= 0 ? false : true;
		isVisible = true;
	}, []);

  const [visible, setVisible] = useState<boolean>(isVisible);

	function handleCopy (codigo: string) {
		navigator.clipboard.writeText(codigo);

		toast({
			title: 'Linha Digitável.',
			description: `Copiado a linha ${codigo}`,
			status: 'info',
			duration: 3000,
			position: 'top-right',
			isClosable: true,
			containerStyle: {
				marginTop: '12'
			}
		})
	};

  return (
    <Container id="modalTitulo" onClick={handleOutSideClick}>
      <Content className={`${visible ? 'active' : ''}`}>
        <Header>
          <button
            onClick={() => {
              setVisible(false);
              setTimeout(() => {
                onClose();
              }, 300);
            }}
          >
            <FiArrowLeft /> Voltar
          </button>
        </Header>

        <TituloContent>
					<TituloItem>
						<Flex><h1>Usuário:</h1>&nbsp;<span>{titulo.user_nome}</span></Flex>
					</TituloItem>

					<TituloItem>
						<h2>Id: <span>{titulo.id}</span></h2>
						<h2>Nro: <span>{titulo.doc}</span></h2>

						<h2>Data:
							<span>
								{format(add(new Date(titulo.data), { days: 1 }),
									" dd'/'MM'/'Y", { locale: ptBR })}
							</span>
						</h2>

						<Flex align="center">
							<h2>Baixado: </h2>
							{titulo.baixado ?
								(<Icon as={FaRegCheckCircle} color="blue.500" w={5} h={5} />) :
								(<Icon as={FaRegCircle} color="gray.300" w={5} h={5} />)
							}
						</Flex>

						<h2>Valor: <span>{formatPrice(titulo.vlr_doc)}</span></h2>
						<h2>Obs: <span>{titulo.obs}</span></h2>
					</TituloItem>

					<TituloItem>
						<h1>Cliente</h1>
						<h2 style={{ color: "#1565C0" }}>{titulo.cliente.nome}</h2>
						<h2>{titulo.cliente.doc}</h2>
						<h2>{titulo.cliente.phone}</h2>
						<h2>{titulo.cliente.cid}</h2>
					</TituloItem>

					<TituloItem>
						<h1>Pagamento</h1>
						<Flex>
							<Image src={titulo.pagamentos.url_pay} objectFit='cover' w='30px' h="25px" />
							<h2>{titulo.pagamentos.name_pay}</h2>
						</Flex>
					</TituloItem>

					{titulo.recorrencia?.ativo && (
						<TituloItem>
							<h1>Recorrencia</h1>
							<h2>{titulo.recorrencia.dia}</h2>
							<h2>{titulo.recorrencia.fim}</h2>
							<h2>{titulo.recorrencia.tempo}</h2>
						</TituloItem>
					)}

					<TituloItem>
						<h1>Operação</h1>
						<h2>{titulo.operacao.nome}</h2>
						<h2>{titulo.operacao.type_nome}</h2>
					</TituloItem>

					{(titulo.documentos && titulo.documentos.length > 0) && (
						<TituloItem>
							<h1>Documentos</h1>
							<Accordion allowMultiple>
								{titulo.documentos.map(doc => {
									return (
										<AccordionItem
											key={doc.id}
											border={0}
											bg="gray.50"
											borderRadius={4}
											mb={2}
										>
											<AccordionButton pl={0} pr={2} _hover={{ background: "transparent" }}>
												<Box as="h2" flex='1' textAlign='left'>
													<span>Cod: {doc.id}</span>
												</Box>
												<AccordionIcon color="blue.500" />
											</AccordionButton>

											<AccordionPanel px={2} pt={-2} pb={4}>
												<h2>Tipo: <span>{doc.type_nome}</span></h2>
												<h2>Parcela: <span>{doc.parcela ? doc.parcela : '--'}</span></h2>
											</AccordionPanel>
										</AccordionItem>
									)
								})}
							</Accordion>
						</TituloItem>
					)}

					{(titulo.conta && titulo.conta.length > 0) && (
						<TituloItem>
							<h1>Contas</h1>
							<Accordion allowMultiple>
								{titulo.conta.map(conta => {
									return (
										<AccordionItem
											key={conta.id}
											border={0}
											bg="gray.50"
											borderRadius={4}
											mb={2}
										>
											<AccordionButton pl={0} pr={2} _hover={{ background: "transparent" }}>
												<Box as="h2" flex='1' textAlign='left'>
													<span>{conta.id} - {conta.nome}</span>
												</Box>
												<AccordionIcon color="blue.500" />
											</AccordionButton>
											
											<AccordionPanel px={2} pt={-2} pb={4}>
												<Flex>
													<h2>Operação: <span>{conta.vlr_cred < 0 ? 'Crédito' : 'Débito'}</span></h2>
													<h2>Valor: <span>{formatPrice(conta.vlr_cred + conta.vlr_deb)}</span></h2>
												</Flex>
												
												<h2>Porcentagem: <span>{conta.percent}%</span></h2>
											</AccordionPanel>
										</AccordionItem>
									)
								})}
							</Accordion>
						</TituloItem>
					)}
					
					{(titulo.parcelas && titulo.parcelas.length > 0) && (
						<TituloItem>
							<h1>Parcelas</h1>
							<Accordion allowMultiple>
								{titulo.parcelas.map(parcela => {
									return (
										<AccordionItem
											key={parcela.id}
											border={0}
											bg="gray.50"
											borderRadius={4}
											mb={2}
										>
											<AccordionButton pl={0} pr={2} _hover={{ background: "transparent" }}>
												<Box as="h2" flex='1' textAlign='left'>
													Parcela: <span>{parcela.parcela}</span>&nbsp;
													Venc: <span>
														{format(add(new Date(parcela.vencimento), { days: 1 }),
															" dd'/'MM'/'Y", { locale: ptBR })}
													</span>
												</Box>
												<AccordionIcon color="blue.500" />
											</AccordionButton>

											<AccordionPanel px={2} pt={-2} pb={4}>
												<Flex>
													<h2>Operação: <span>{parcela.vlr_cred > 0 ? 'Crédito' : 'Débito'}</span></h2>
													<h2>Valor: <span>{formatPrice(parcela.vlr_cred + parcela.vlr_deb)}</span></h2>
												</Flex>

												<Flex>
													<h2>Pago: <span>{formatPrice(parcela.pago)}</span></h2>
													<h2>Amortizado: <span>{formatPrice(parcela.amortizado)}</span></h2>
												</Flex>

												<h2>Desconto: <span>{formatPrice(parcela.desconto)}</span></h2>
												

												<Flex>
													<h2>Juros: <span>{formatPrice(parcela.juros)}</span></h2>
													<h2>Multa: <span>{formatPrice(parcela.multa)}</span></h2>
												</Flex>

												{parcela.taxa_card || parcela.taxa_antecip && (
													<Flex>
														{parcela.taxa_card && (
															<h2>Taxa Cartão: <span>{formatPrice(parcela.taxa_card)}</span></h2>
														)}

														{parcela.taxa_antecip && (
															<h2>Taxa Antecipação: <span>{formatPrice(parcela.taxa_antecip)}</span></h2>
														)}
													</Flex>
												)}

												<h2>Outra Despesa: <span>{formatPrice(parcela.outra_desp)}</span></h2>

												<Flex>
													<h2>Saldo: <span>{formatPrice(parcela.saldo_cred + parcela.saldo_deb)}</span></h2>
												</Flex>
												
												{parcela.data_baixa && (
													<h2>Data Baixa:
														<span>
															{format(add(new Date(parcela.data_baixa), { days: 1 }),
																" dd'/'MM'/'Y", { locale: ptBR })}
														</span>
													</h2>
												)}
												
												{parcela.boleto && (
													<Flex flexDir="column">
														<Text color="gray.700" fontWeight={500}>Boleto:</Text>

														<Flex
															flexDir="column"
															borderWidth={2}
															borderColor="gray.100"
															borderRadius={8}
															ml={2}
															py={1}
															mb={1}
														>
															<h2>Nosso Nro: <span>{parcela.boleto.nosso_nro}</span></h2>
															<h2>Linha Dig:&nbsp;
																<Text
																	as="button"
																	onClick={() => handleCopy(parcela.boleto.linha_dig)}
																>
																	<span style={{ color: '#1a83ff' }}>{parcela.boleto.linha_dig}</span>	
																</Text>	
															</h2>
															<h2>Status: <span>{parcela.boleto.registro}</span></h2>
															{parcela.boleto.venc && (
																<h2>Vencimento: 
																	<span>
																		{format(add(new Date(parcela.boleto.venc), { days: 1 }),
																			"dd'/'MM'/'Y", { locale: ptBR })}
																</span>
																</h2>
															)}
															<h2>Valor: <span>{formatPrice(parcela.boleto.vlr)}</span></h2>
															<Flex>
															<h2>Multa: <span>{formatPrice(parcela.boleto.multa)}</span></h2>
															<h2>Mora: <span>{formatPrice(parcela.boleto.mora)}</span></h2>
															</Flex>
														</Flex>
													</Flex>
												)}
											</AccordionPanel>
										</AccordionItem>
									)
								})}
							</Accordion>
						</TituloItem>
					)}

					{(titulo.baixas && titulo.baixas.length > 0) && (
						<TituloItem>
							<h1>Baixas</h1>
							<Accordion allowMultiple>
								{titulo.baixas?.map(baixa => {
									return (
										<AccordionItem
											key={baixa.id}
											border={0}
											bg="gray.50"
											borderRadius={4}
											mb={2}
										>
											<AccordionButton pl={0} pr={2} _hover={{ background: "transparent" }}>
												<Box as="h2" flex='1' textAlign='left'>
													Parcela: <span>{baixa.parcela}</span>
												</Box>
												<AccordionIcon color="blue.500" />
											</AccordionButton>
											
											<AccordionPanel px={2} pt={-2} pb={4}>
												<h2>Data:
													<span>
														{format(add(new Date(baixa.data), { days: 1 }),
															" dd'/'MM'/'Y", { locale: ptBR })}
													</span>
												</h2>

												<h2>Usuário: <span>{baixa.user_nome}</span></h2>

												<Flex>
													<h2>Operação: <span>{baixa.vlr_cred > 0 ? 'Crédito' : 'Crédito'}</span></h2>
													<h2>Valor: <span>{formatPrice(baixa.vlr_cred + baixa.vlr_deb)}</span></h2>
												</Flex>

												<h2>Desconto: <span>{formatPrice(baixa.desconto)}</span></h2>

												<Flex>
													<h2>Juros: <span>{formatPrice(baixa.juros)}</span></h2>
													<h2>Multa: <span>{formatPrice(baixa.multa)}</span></h2>
												</Flex>

												<Flex>
													<h2>Taxa Cartão: <span>{formatPrice(baixa.taxa_card)}</span></h2>
													<h2>Taxa Antecipação: <span>{formatPrice(baixa.taxa_antecip)}</span></h2>
												</Flex>

												<h2>Outra Despesa: <span>{formatPrice(baixa.outra_desp)}</span></h2>

												<Flex>
													<h2>Valor Pago: <span>{formatPrice(baixa.vlr_pago_cred + baixa.vlr_pago_deb)}</span></h2>
												</Flex>

												<Flex flexDir="column">
													<Text color="gray.700" fontWeight={500}>Forma Pagamento:</Text>

													{baixa.pay.map(payy => {
														return (
															<Flex
																flexDir="column"
																borderWidth={2}
																borderColor="gray.100"
																borderRadius={8}
																ml={2}
																py={1}
																mb={1}
															>
																<Flex align="center" pl={2}>
																	<Image src={payy.url_pay} objectFit='cover' w='25px' h="20px" />
																	<h2>{payy.name_pay}</h2>&nbsp;
																	{payy.cod_auth && (
																		<h2>Cod. <span>{payy.cod_auth}</span></h2>
																	)}
																</Flex>
															</Flex>
														)
													})}
												</Flex>
											</AccordionPanel>
										</AccordionItem>
									)
								})}
							</Accordion>
						</TituloItem>
					)}

					{(titulo.lancamentos && titulo.lancamentos.length > 0) && (
						<TituloItem>
							<h1>Lançamentos</h1>
							<Accordion allowMultiple>
								{titulo.lancamentos?.map(lancamento => {
									return (
										<AccordionItem
											key={lancamento.id}
											border={0}
											bg="gray.50"
											borderRadius={4}
											mb={2}
										>
											<AccordionButton pl={0} pr={2} _hover={{ background: "transparent" }}>
												<Box as="h2" flex='1' textAlign='left' pl={0}>
													Data:
													<span>
														{format(add(new Date(lancamento.data), { days: 1 }),
															" dd'/'MM'/'Y", { locale: ptBR })}
													</span>
												</Box>
												<AccordionIcon color="blue.500" />
											</AccordionButton>

											<AccordionPanel px={2} pt={-2} pb={4}>
												<Flex>
													<h2>Operação: <span>{lancamento.vlr_cred > 0 ? 'Crédito' : 'Débito'}</span></h2>
													<h2>Valor: <span>{formatPrice(lancamento.vlr_cred + lancamento.vlr_deb)}</span></h2>
												</Flex>

												<h2>Desconto: <span>{formatPrice(lancamento.desconto)}</span></h2>

												<Flex>
													<h2>Juros: <span>{formatPrice(lancamento.juros)}</span></h2>
													<h2>Multa: <span>{formatPrice(lancamento.multa)}</span></h2>
												</Flex>

												<Flex>
													<h2>Taxa Cartão: <span>{formatPrice(lancamento.taxa_card)}</span></h2>
													<h2>Taxa Antecipação: <span>{formatPrice(lancamento.taxa_antecip)}</span></h2>
												</Flex>

												<Flex>
													<h2>Valor Pago: <span>{formatPrice(lancamento.vlr_pago_cred + lancamento.vlr_pago_deb)}</span></h2>
												</Flex>

												{/* CAIXA */}
												<Flex flexDir="column">
													<Text color="gray.700" fontWeight={500}>Caixa:</Text>

													<Flex
														flexDir="column"
														borderWidth={2}
														borderColor="gray.100"
														borderRadius={8}
														ml={2}
														py={1}
														mb={1}
													>
														<h2>Usuário: <span>{lancamento.caixa.user_nome}</span></h2>
														<h2><span>{lancamento.caixa.nome}</span></h2>
														<h2><span>{lancamento.caixa.type_nome}</span></h2>
														<Flex align="center">
															<h2>Compensado: </h2>
																{lancamento.caixa.compensado ? 
																	(<Icon as={FaRegCheckCircle} color="blue.500" w={5} h={5} />) : 
																	(<Icon as={FaRegCircle} color="gray.300" w={5} h={5} />)
																}
														</Flex>
													</Flex>
												</Flex>

												{(lancamento.conta && lancamento.conta.length > 0) && (
													<Flex flexDir="column">
														<Text color="gray.700" fontWeight={500}>Contas:</Text>

														{lancamento.conta.map(con => {
															return (
																<Flex
																	key={con.id}
																	flexDir="column"
																	borderWidth={2}
																	borderColor="gray.100"
																	borderRadius={8}
																	ml={2}
																	py={1}
																	mb={1}
																>
																	<h2><span>{con.id} - {con.nome}</span></h2>

																	<Flex>
																		<h2>Operação: <span>{con.vlr_cred > 0 ? 'Crédito' : 'Débito'}</span></h2>
																		<h2>Valor: <span>{formatPrice(con.vlr_cred + con.vlr_deb)}</span></h2>
																	</Flex>
																</Flex>
															)
														})}
													</Flex>	
												)}

												<Flex flexDir="column">
													<Text color="gray.700" fontWeight={500}>Forma Pagamento:</Text>

													<Flex
														flexDir="column"
														borderWidth={2}
														borderColor="gray.100"
														borderRadius={8}
														ml={2}
														py={1}
														mb={1}
													>
														<h2>Cod: <span>{lancamento.pay.cod_auth}</span></h2>

														<Flex align="center" pl={2}>
															<Image src={lancamento.pay.url_pay} objectFit='cover' w='25px' h="20px" />
															<h2>{lancamento.pay.name_pay}</h2>
														</Flex>
													</Flex>
												</Flex>
											</AccordionPanel>
										</AccordionItem>
									)
								})}
							</Accordion>
						</TituloItem>
					)}
        </TituloContent>
      </Content>
    </Container>
  );
};

export default ModalTitulo;
